import * as _ from 'lodash-es';
import type { ScrollBlot } from 'parchment';
import Block, { BlockEmbed } from 'quill/blots/block';
import type { Op } from 'quill/core';
import Quill, { Delta } from 'quill/core';
import type { Range } from 'quill/core/selection';
import QuillClipboard from 'quill/modules/clipboard';
import getApolloClient from '../app/apollo/getApolloClient';
import AttachmentUploader, {
  CopyFileFromUrl,
} from '../utils/AttachmentUploader';
import getFeature from '../utils/features';
import request from '../utils/network/request';
import includesExtension from '../utils/url/includesExtension';
import safeParseUrl from '../utils/url/safeParseUrl';
import { removeAssetTokenFromSourceUrl } from './AssetTokenContext';
import AssetUploader from './AssetUploader';
import { getRangeSaver } from './RangeSaver';
import {
  LINK_TYPES,
  LINK_TYPE_ATTRIBUTE,
  MENTION_TEXT_CLIPBOARD_ATTRIBUTE,
  READONLY_CHANGE_EVENT,
} from './constants';
import matchEmbed from './embed/matchEmbed';
import Annotation from './formats/Annotation';
import type { AttachmentFile } from './formats/Attachment';
import Attachment from './formats/Attachment';
import AttachmentMention from './formats/AttachmentMention';
import Card from './formats/Card';
import Comment from './formats/Comment';
import FileBlot from './formats/FileBlot';
import HeaderId from './formats/HeaderId';
import IFrame from './formats/IFrame';
import type ImageBlotType from './formats/ImageBlot';
import Reaction from './formats/Reaction';
import Video from './formats/Video';
import { parseAttachmentsHtml } from './formats/utils/attachmentsHtml';
import getSelectedEmbed from './getSelectedEmbed';
import { isIFrameSrcAllowed } from './iframe/IFrameComponent';
import type { CarouselItem } from './image/types';
import convertFromNode from './table/convertFromNode';
import UrlRule from './utils/UrlRule';
import fetchPostAnchorTitle from './utils/fetchPostAnchorTitle';
import listenBody from './utils/listenBody';
import loadMarkdownToHTML, { isMarkdown } from './utils/loadMarkdownToHTML';
import matchEntityLink from './utils/matchEntityLink';
import matchImage from './utils/matchImage';
import matchMention from './utils/matchMention';
import matchVideo from './utils/matchVideo';

const getMentionAttributesFromNode = (node: Node) => {
  if (!(node instanceof HTMLElement)) {
    return {};
  }

  const mentionText = node.getAttribute(MENTION_TEXT_CLIPBOARD_ATTRIBUTE);

  const comment = Comment.value(node);
  const annotation = Annotation.value(node);
  const reaction = Reaction.value(node);

  return {
    ...(comment ? { comment } : {}),
    ...(annotation ? { annotation } : {}),
    ...(reaction ? { reaction } : {}),
    ...(mentionText ? { 'mention-text': mentionText } : {}),
  };
};

function matchSource(
  text: string,
  sources: { regex: RegExp[]; url: (id: string) => string }[],
): string | null {
  let matchUrl: string | null = null;
  _.forEach(sources, ({ regex, url }) => {
    regex.find((r) => {
      const match = text.match(r);
      if (match) {
        matchUrl = url(match[1] as string);
      }
      return match != null;
    });
    return matchUrl == null;
  });
  return matchUrl;
}

function matchFollow(text: string) {
  const followSources = [
    {
      regex: [/^https:\/\/drive\.google\.com\/open\?id=([^/]+)/],
      url: (id: string) => `https://drive.google.com/open?id=${id}`,
    },
  ];
  return matchSource(text, followSources);
}

const POST_EMBED_RULE = new UrlRule({
  host: window.location.host,
  path: '/embed/:name-:id(\\w+)',
});

function matchPostEmbed(link: string | null) {
  if (!link) return null;

  const url = safeParseUrl(link);
  const matched = url ? POST_EMBED_RULE.match(url) : {};

  return matched?.id
    ? new Delta().insert({ embed: { id: matched.id, type: 'post' } })
    : null;
}

type MatcherDef = Clipboard['matchers'][number];

const imageMatcher: MatcherDef = [
  'IMG,FIGURE',
  (node, delta, scroll) => {
    const ImageBlot = scroll.query('image') as null | typeof ImageBlotType;
    const value =
      ImageBlot && Object.prototype.isPrototypeOf.call(BlockEmbed, ImageBlot)
        ? ImageBlot.value(node as HTMLElement)
        : null;

    const images = _.compact(value);

    if (ImageBlot && images.length) {
      const assetUploader = AssetUploader.getForBlot(scroll);
      const adjustedImages = ImageBlot.addImageIdsIfNeeded(
        images.map<CarouselItem>(({ source, ...rest }) =>
          assetUploader?.canReuseFileUrl(source)
            ? { ...rest, source: removeAssetTokenFromSourceUrl(source) }
            : {
                ...rest,
                uploadPreviewSource: source,
                uploadPayload: source,
              },
        ),
      );

      return new Delta().insert(
        { image: adjustedImages },
        {
          ...ImageBlot.formats(node as HTMLElement),
          ...delta.ops[0]?.attributes,
        },
      );
    }

    return delta;
  },
];

const attachmentMatcher: MatcherDef = [
  '.ql-attachment',
  (node, delta, scroll) => {
    const isBlotTypeSupported = !!scroll.query(Attachment.blotName);
    const attachments =
      isBlotTypeSupported && parseAndUploadPastedAttachments(node, scroll);
    return attachments
      ? new Delta().insert({ [Attachment.blotName]: attachments })
      : delta;
  },
];

const attachmentMentionMatcher: MatcherDef = [
  '.ql-attachment-mention',
  (node, delta, scroll) => {
    const isBlotTypeSupported = !!scroll.query(AttachmentMention.blotName);
    const attachments =
      isBlotTypeSupported && parseAndUploadPastedAttachments(node, scroll);
    return attachments
      ? new Delta().insert(
          { [AttachmentMention.blotName]: attachments },
          getMentionAttributesFromNode(node),
        )
      : delta;
  },
];

const fileEmbedMatcher: MatcherDef = [
  '.ql-file',
  (node, delta, scroll) => {
    const isBlotTypeSupported = !!scroll.query(FileBlot.blotName);
    const attachments =
      isBlotTypeSupported && parseAndUploadPastedAttachments(node, scroll);
    return attachments && attachments.length === 1
      ? new Delta().insert({ [FileBlot.blotName]: attachments[0] })
      : delta;
  },
];

function parseAndUploadPastedAttachments(node: Node, scroll: ScrollBlot) {
  if (!(node instanceof HTMLElement)) {
    return null;
  }

  const attachments = parseAttachmentsHtml(node);
  if (!attachments.length) {
    return null;
  }

  const assetUploader = AssetUploader.getForBlot(scroll);
  const filesToUpload = new Map(
    attachments
      .filter(
        (item) => assetUploader?.canReuseFileUrl(item.urlWithoutJWT) === false,
      )
      .map((item) => [
        item,
        new CopyFileFromUrl(item.urlWithJWT, item.name, item.size),
      ]),
  );

  AttachmentUploader.uploadFiles([...filesToUpload.values()], scroll);

  return attachments.map((item): AttachmentFile => {
    const fileToUpload = filesToUpload.get(item);
    return {
      source: fileToUpload
        ? AttachmentUploader.getIdForFile(fileToUpload)
        : item.urlWithoutJWT,
      name: item.name,
      size: item.size,
    };
  });
}

const codeEmbedMatcher: MatcherDef = [
  'PRE',
  (node, delta, scroll) => {
    const match = scroll.query('code-embed');
    if (!match) {
      return delta;
    }

    // innerText doesn't convert <br/> to \n when node is not rendered.
    (node as Element).querySelectorAll('br').forEach((br) => {
      const span = br.ownerDocument.createElement('span');
      span.innerHTML = '\n';
      br.replaceWith(span);
    });
    const { innerText } = node as HTMLElement;
    const code = innerText.endsWith('\n') ? innerText : `${innerText}\n`;
    // @ts-expect-error
    const language = match?.formats(node)?.language;
    const attributes = language ? { language } : undefined;
    return new Delta().insert(
      { 'code-embed': new Delta().insert(code).ops },
      attributes,
    );
  },
];

const tableMatcher: MatcherDef = [
  'TABLE',
  (node, delta, scroll) => {
    if (scroll.query('table-embed')) {
      const value = convertFromNode(node as HTMLTableElement);
      return new Delta().insert({ 'table-embed': value });
    }
    return delta;
  },
];

const getLinkMatcher = (
  allowedMentionTypes: Set<string> | undefined,
): MatcherDef => {
  return [
    'a',
    (node, delta, scroll) => {
      const href = (node as Element).getAttribute('href');
      if (!href) return delta;
      const linkType = (node as Element).getAttribute(LINK_TYPE_ATTRIBUTE);
      if (linkType === LINK_TYPES.embed) {
        const embed = scroll.query('embed') && matchEmbed(href);
        if (embed) {
          return new Delta().insert({ embed: embed.value }, embed.attributes);
        }

        const mention = scroll.query('mention') && matchMention(href);
        if (mention?.modes.includes('embed')) {
          return new Delta().insert({ embed: mention.value });
        }

        const video = scroll.query(Video.blotName) && matchVideo(href);
        if (video) {
          return new Delta().insert({ [Video.blotName]: video });
        }
      }
      if (linkType === LINK_TYPES.iframe && scroll.query(IFrame.blotName)) {
        return new Delta().insert({ [IFrame.blotName]: href });
      }

      if (linkType === LINK_TYPES.card && scroll.query(Card.blotName)) {
        const value = matchEntityLink(href);
        if (value) {
          return new Delta().insert({ [Card.blotName]: value });
        }
      }

      if (!linkType || linkType === LINK_TYPES.mention) {
        // Only match mentions if the link is copied from external sites
        // or it is a mention originally.
        const mention = scroll.query('mention') && matchMention(href);
        if (
          mention?.modes.includes('mention') &&
          (!allowedMentionTypes || allowedMentionTypes.has(mention.value.type))
        ) {
          return new Delta().insert(
            { mention: mention.value },
            getMentionAttributesFromNode(node),
          );
        }
      }
      return delta;
    },
  ];
};

const isLink = (text: string) => {
  return /^https?[^\s]+$/.test(text) && safeParseUrl(text)?.hostname != null;
};
const isImage = (text: string) => {
  return includesExtension(text, ['png', 'gif', 'jpg', 'jpeg', 'svg']);
};

const getIframeSrc = (trimmedText: string) => {
  if (trimmedText.startsWith('<') && trimmedText.match(/<iframe\s/i)) {
    const doc = new DOMParser().parseFromString(trimmedText, 'text/html');
    const iframe = doc.querySelector('iframe');
    const src = iframe?.getAttribute('src');
    if (src) {
      const relativeProtocol = '//';
      return src.startsWith(relativeProtocol) ? `https:${src}` : src;
    }
  }
  return null;
};

export type ClipboardHooks = {
  beforePaste?: (html: string) => string;
  beforeCut?: (html: string) => string;
};

type Options = QuillClipboard['options'] & {
  clipboardHooks?: ClipboardHooks;
  publicView?: boolean;
  allowedMentionTypes?: Set<string>;
};

class Clipboard extends QuillClipboard {
  private shiftKey: boolean;
  private markdownToHTML?: (markdown: string) => string | null;
  private clipboardHooks: ClipboardHooks;
  private publicView: boolean = false;
  private allowedMentionTypes: Set<string> | undefined;

  constructor(quill: Quill, options: Options) {
    const matchers = [
      imageMatcher,
      getLinkMatcher(options.allowedMentionTypes),
      tableMatcher,
      codeEmbedMatcher,
      attachmentMatcher,
      attachmentMentionMatcher,
      fileEmbedMatcher,
    ].map(
      ([tagName, matcher]) =>
        [tagName, _.partialRight(matcher, quill)] as MatcherDef,
    );

    super(quill, { matchers });

    this.clipboardHooks = options.clipboardHooks ?? {};
    this.publicView = !!options.publicView;
    this.allowedMentionTypes = options.allowedMentionTypes;
    // When an embed is selected, the selection will be moved to embedSelectionContainer.
    // In this case, the target of clipboard events are set to body element because the selection
    // is invisible.
    // See the step 6 in https://w3c.github.io/clipboard-apis/#to-fire-a-clipboard-event.
    listenBody('copy', (e) => this.onCaptureEmbedCopy(e, false), quill);
    listenBody('cut', (e) => this.onCaptureEmbedCopy(e, true), quill);
    listenBody('paste', this.onCaptureEmbedPaste.bind(this), quill);

    this.loadMarkdownToHTML();

    this.shiftKey = false;
    this.quill.root.addEventListener('keydown', (e) => {
      if (e.key === 'Shift') {
        this.shiftKey = true;
      }
    });
    this.quill.root.addEventListener('keyup', (e) => {
      if (e.key === 'Shift') {
        this.shiftKey = false;
      }
    });
  }

  loadMarkdownToHTML() {
    const load = () => {
      loadMarkdownToHTML().then((markdownToHTML) => {
        this.markdownToHTML = markdownToHTML;
      });
    };

    if (this.quill.options.readOnly) {
      this.quill.on(READONLY_CHANGE_EVENT, (readOnly: boolean) => {
        if (!readOnly) load();
      });
    } else {
      load();
    }
  }

  isEmbedSelected() {
    const { quill } = this;
    if (quill.root.contains(document.activeElement)) {
      return !!getSelectedEmbed(quill);
    }
    return false;
  }

  onCaptureEmbedCopy(e: ClipboardEvent, isCut = false) {
    if (this.quill.root.contains(e.target as Node)) return;
    if (!this.isEmbedSelected()) return;
    this.onCaptureCopy(e, isCut);
  }

  onCaptureEmbedPaste(e: ClipboardEvent) {
    if (this.quill.root.contains(e.target as Node)) return;
    if (!this.isEmbedSelected()) return;
    this.onCapturePaste(e);
  }

  onCaptureCopy(e: ClipboardEvent, isCut = false) {
    if (e.defaultPrevented) return;
    const embed = getSelectedEmbed(this.quill);
    if (
      embed != null &&
      'onCaptureCopy' in embed &&
      typeof embed.onCaptureCopy === 'function'
    ) {
      embed.onCaptureCopy(e, isCut, this.clipboardHooks);
    }
    super.onCaptureCopy(e, isCut);
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async onPaste(range: Range, { text, html }: { text: string; html: string }) {
    const { quill } = this;
    const isEOLSelected = range.length === 1 && quill.getText(range) === '\n';

    if (!isEOLSelected && range.length && isLink(text)) {
      const lines = quill.getLines(range);
      if (lines.length === 1 && lines[0] instanceof Block) {
        quill.formatText(
          range.index,
          range.length,
          'link',
          text,
          Quill.sources.USER,
        );
        return;
      }
    }

    // When double-click or context menu click at the end of a line, the newline char
    // will be selected. In this case, it's more intuitive to keep the newline char.
    const targetRange = isEOLSelected
      ? { index: range.index, length: 0 }
      : range;

    const formats = this.quill.getFormat(targetRange.index);

    const { beforePaste } = this.clipboardHooks;
    const pastedDelta = this.convert(
      { text, html: beforePaste ? beforePaste(html) : html },
      formats,
    );

    const embedConvertedFromLink = isLink(text)
      ? // @ts-expect-error
        pastedDelta.ops.find((op) => op.insert?.mention || op.insert?.embed)
      : null;
    if (embedConvertedFromLink) {
      // @ts-expect-error
      if (embedConvertedFromLink.insert?.mention?.type === 'post') {
        const client = getApolloClient();
        if (client) {
          const title = await fetchPostAnchorTitle(
            client,
            // @ts-expect-error
            embedConvertedFromLink.insert?.mention.id,
          );
          if (title) {
            embedConvertedFromLink.attributes = { 'mention-text': title };
          }
        }
      }
      this.quill.updateContents(
        new Delta()
          .retain(targetRange.index)
          .delete(targetRange.length)
          .insert(text, { link: text }),
        Quill.sources.USER,
      );
      this.quill.history.cutoff();
      // TODO: Use `updateContents()` directly when the following issues are fixed:
      // https://github.com/quilljs/quill/issues/3620
      // https://github.com/quilljs/quill/issues/3623
      this.quill.deleteText(targetRange.index, text.length, Quill.sources.USER);
      const updated = this.quill.updateContents(
        new Delta().retain(targetRange.index).concat(pastedDelta),
        Quill.sources.USER,
      );
      this.quill.setSelection(
        targetRange.index + updated.changeLength(),
        Quill.sources.SILENT,
      );
    } else {
      const delta = new Delta()
        .retain(targetRange.index)
        .delete(targetRange.length)
        .concat(pastedDelta);
      this.quill.history.cutoff();
      this.quill.updateContents(delta, Quill.sources.USER);
      this.quill.setSelection(
        targetRange.index + pastedDelta.length(),
        Quill.sources.SILENT,
      );
    }
    this.quill.scrollSelectionIntoView();
  }

  isUnstyledText(delta: Delta) {
    const { ops } = delta;
    if (ops.length > 1) return false;
    const [op] = ops;
    if (!op) return true;
    if (typeof op.insert !== 'string') return false;

    const hasSupportedAttribute = Object.keys(op.attributes || {}).some((key) =>
      this.quill.scroll.query(key),
    );
    return !hasSupportedAttribute;
  }

  convert(
    { html, text }: { html?: string | undefined; text?: string | undefined },
    formats: Record<string, unknown> | undefined = {},
  ): Delta {
    const trimmed = (text || '').trim();
    const isTextLink = isLink(trimmed);
    if (isTextLink && !this.publicView) {
      const operation = (() => {
        const mention = matchMention(trimmed);
        if (
          !mention ||
          (this.allowedMentionTypes &&
            !this.allowedMentionTypes.has(mention.value.type))
        ) {
          return null;
        }

        const supportedMode = mention.modes.find((mode) =>
          this.quill.scroll.query(mode),
        );
        if (!supportedMode) return null;

        return {
          [supportedMode]: mention.value,
        };
      })();

      if (operation) {
        return new Delta().insert(operation).insert(' ');
      }
    }
    if (formats.list == null && !this.publicView) {
      const iframeSrc = getIframeSrc(trimmed);

      const postDelta = matchPostEmbed(iframeSrc);
      if (postDelta) {
        return postDelta;
      }

      const allowedIframeSrc =
        iframeSrc && isIFrameSrcAllowed(iframeSrc) ? iframeSrc : null;

      const embedDelta = this.matchEmbed(
        isTextLink ? trimmed : allowedIframeSrc,
      );
      if (embedDelta) {
        return embedDelta;
      }
      const iframeDelta = this.matchIframe(allowedIframeSrc);
      if (iframeDelta) {
        return iframeDelta;
      }
    }
    if (isTextLink) {
      const range = getRangeSaver(this.quill).getLast();
      const follow = matchFollow(trimmed);
      if (follow) {
        request('/api/follow-url', 'GET', { url: follow })
          // @ts-expect-error
          .then(({ url }) => {
            this.onPaste(range, { html: url, text: url });
          })
          .catch(() => {
            // Paste link as plain text
            this.onPaste(range, { html: trimmed, text: '' });
          });
        return new Delta();
      }
      if (this.quill.scroll.query('image') && isImage(trimmed)) {
        const image = matchImage(trimmed);
        // @ts-expect-error
        this.quill.uploader.upload(range, [image]);
        return new Delta();
      }
      return new Delta().insert(trimmed, { link: trimmed });
    }

    let convertedDelta = super.convert({ html, text }, formats);

    if (
      !this.shiftKey &&
      this.isUnstyledText(convertedDelta) &&
      this.markdownToHTML &&
      isMarkdown(text)
    ) {
      convertedDelta = super.convert(
        { html: this.markdownToHTML(text) ?? undefined, text },
        formats,
      );
    }

    return convertedDelta.reduce((delta: Delta, op: Op) => {
      if (op.attributes?.diff) {
        // @ts-expect-error
        return delta.insert(op.insert, _.omit(op.attributes, 'diff'));
      }
      return delta.push(op);
    }, new Delta());
  }

  onCopy(range: Range, isCut: boolean) {
    const { html, text } = super.onCopy(range, isCut);
    const div = this.quill.root.ownerDocument.createElement('div');
    div.innerHTML = html;
    const links = div.querySelectorAll(`a:not([${LINK_TYPE_ATTRIBUTE}])`);
    links.forEach((node) =>
      node.setAttribute(LINK_TYPE_ATTRIBUTE, LINK_TYPES.text),
    );

    if (!isCut) {
      const headerIds = div.querySelectorAll(`[${HeaderId.attrName}]`);
      headerIds.forEach((node) => {
        node.removeAttribute(HeaderId.attrName);
        node.removeAttribute('id');
      });
    }

    const cleanedHtml = div.innerHTML;

    const { beforeCut } = this.clipboardHooks;
    return {
      html: isCut && beforeCut ? beforeCut(cleanedHtml) : cleanedHtml,
      text,
    };
  }

  matchEmbed(link: string | null) {
    if (!link) return null;
    const embed = this.quill.scroll.query('embed') && matchEmbed(link);
    if (embed) {
      return new Delta().insert({ embed: embed.value }, embed.attributes);
    }
    const video = this.quill.scroll.query(Video.blotName) && matchVideo(link);
    if (video) {
      return new Delta().insert({ video });
    }
    return null;
  }

  matchIframe(link: string | null) {
    if (!link) return null;
    if (
      getFeature('editor_iframe') &&
      this.quill.scroll.query(IFrame.blotName)
    ) {
      const iframeDelta = new Delta().insert({ [IFrame.blotName]: link });
      const { allowedEmbedHosts } = window.__slab.PUBLIC_ORG.settings;
      if (!allowedEmbedHosts) return null;

      if (allowedEmbedHosts.includes('*')) {
        return iframeDelta;
      }
      const { host } = new URL(link);
      if (allowedEmbedHosts.includes(host)) {
        return iframeDelta;
      }
    }
    return null;
  }
}

export default Clipboard;
